/**
 * DiagnosticHeadline — the run Summary tab's lead block once a failure
 * diagnostic has completed.
 *
 * Replaces the plain red "Step failed" headline with the diagnostic's verdict:
 * category + one-sentence thesis, followed by the root-cause
 * timeline as a timestamped, click-to-seek "Key evidence" list. Clicking an
 * event seeks the video and flips the player's scrubber to the diagnostic
 * timeline (where the same event is marked). A ghost "View full analysis" link
 * jumps to the full report in the right column.
 *
 * @module ui/pages/FlowDesigner/runs/DiagnosticHeadline
 */
import React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, ArrowsClockwise, Sparkle } from '@phosphor-icons/react';
import { Text, TagBadge, CollapsibleSection, formatTimelineTimestamp } from '@chatsdet/ui';
import type { FailureDiagnostic } from '@chatsdet/types/issues';
import { formatShortDate, repeatedFromSuffix } from '../../../../lib/formatShortDate';
import {
  CATEGORY_TONE,
  CATEGORY_ICONS,
  diagnosticClassificationLabel,
  rootCauseSeverityDotClass,
} from '../../../components/issues/diagnosticDisplay';

export interface DiagnosticHeadlineProps {
  diagnostic: FailureDiagnostic;
  /**
   * The workflow this run belongs to. When set and the diagnosis drafted a
   * proposal, the headline shows a "Proposed fix" callout that deep-links to the
   * proposal's review in the flow designer.
   */
  workflowId?: string | null;
  /** Seek the video to a root-cause event (and switch the scrubber to the diagnostic timeline). */
  onSelectEvent?: (timestampMs: number) => void;
  /** Scroll the right column to the full root-cause report. */
  onViewAnalysis?: () => void;
}

export const DiagnosticHeadline: React.FC<DiagnosticHeadlineProps> = ({
  diagnostic,
  workflowId,
  onSelectEvent,
  onViewAnalysis,
}) => {
  const events = diagnostic.rootCauseTimeline ?? [];
  const smokingGun = diagnostic.smokingGun;
  const proposalId = diagnostic.proposalId;
  const repeatOf = diagnostic.triage?.matchedIssueShortcode ? undefined : diagnostic.triage?.repeatOf;

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <TagBadge
          name={diagnosticClassificationLabel(diagnostic)}
          color={CATEGORY_TONE[diagnostic.category] ?? 'gray'}
          icon={CATEGORY_ICONS[diagnostic.category]}
          hideDot
        />
      </div>

      <Text size="sm" weight="medium" className="mt-2 leading-relaxed">
        {diagnostic.title}
      </Text>

      {/* Triage — a repeat failure that was confirmed (cheaply) as the same
          root cause already diagnosed on `matchedIssueShortcode`, rather than
          freshly re-analyzed. The category/title above are inherited, not
          re-derived, on this run. */}
      {diagnostic.triage ? (
        <div className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2">
          <div className="flex items-center gap-1.5">
            <ArrowsClockwise
              className="h-3.5 w-3.5 text-muted-foreground"
              weight="bold"
              aria-hidden="true"
            />
            <Text size="xs" weight="medium" className="uppercase tracking-wide text-muted-foreground">
              {repeatOf ? `Same failure as ${formatShortDate(repeatOf.runCreatedAt)}` : 'Confirmed repeat failure'}
            </Text>
          </div>
          <Text size="sm" className="mt-1 leading-snug">
            {diagnostic.triage.matchedIssueId && diagnostic.triage.matchedIssueShortcode ? (
              <>
                Confirmed the same failure as{' '}
                <Link
                  to="/issues"
                  search={{ issueId: diagnostic.triage.matchedIssueId }}
                  className="font-medium text-primary hover:underline"
                >
                  {diagnostic.triage.matchedIssueShortcode}
                </Link>
              </>
            ) : repeatOf ? (
              <>
                No new analysis ran — same error at the same place in the test code.{' '}
                <Link
                  to="/runs/external/$runId/item/$itemId"
                  params={{ runId: repeatOf.runId, itemId: repeatOf.itemId }}
                  className="font-medium text-primary hover:underline"
                >
                  View that run
                </Link>
              </>
            ) : (
              'Confirmed the same failure already diagnosed on this flow'
            )}
          </Text>
          {repeatOf ? null : (
            <Text size="xs" className="mt-1 text-muted-foreground leading-snug">
              {diagnostic.triage.reasoning}
            </Text>
          )}
        </div>
      ) : null}

      {/* Proposed fix — the agent drafted a reviewable flow change for this failure. */}
      {proposalId && workflowId ? (
        <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
          <div className="flex items-center gap-1.5">
            <Sparkle className="h-3.5 w-3.5 text-primary" weight="fill" aria-hidden="true" />
            <Text size="xs" weight="medium" className="uppercase tracking-wide text-primary">
              Proposed fix
            </Text>
          </div>
          {diagnostic.proposalSummary ? (
            <Text size="sm" className="mt-1 leading-snug">
              {diagnostic.proposalSummary}
            </Text>
          ) : null}
          <Link
            to="/flows/$workflowId"
            params={{ workflowId }}
            search={{ proposalId }}
            className="mt-1.5 inline-flex items-center gap-1 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Review proposed change
            <ArrowRight size={12} />
          </Link>
        </div>
      ) : null}

      {/* Key Evidence = the single smoking gun (matches the full report's label). */}
      {smokingGun && (
        <CollapsibleSection
          label={`Key evidence${repeatedFromSuffix(repeatOf)}`}
          defaultExpanded
          className="mt-3"
        >
          <div className="border-l-2 border-destructive pl-3 py-1.5 bg-destructive/5 rounded-r">
            <Text size="sm">{smokingGun.description}</Text>
            {smokingGun.detail ? (
              <Text size="xs" className="text-muted-foreground mt-1 font-mono break-all">
                {smokingGun.detail}
              </Text>
            ) : null}
          </div>
        </CollapsibleSection>
      )}

      {/* Timeline = the ordered, timestamped, jump-to-video watch-list. */}
      {events.length > 0 && (
        <CollapsibleSection
          label="Timeline"
          count={events.length}
          defaultExpanded
          className={smokingGun ? undefined : 'mt-3'}
        >
          <div className="space-y-1">
            {events.map((event, i) => {
              const row = (
                <>
                  <span
                    className={`mt-[5px] h-2 w-2 shrink-0 rounded-full ${rootCauseSeverityDotClass(
                      event.severity
                    )}`}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <Text size="sm" className="leading-snug">
                      {event.title}
                    </Text>
                    {event.detail ? (
                      <Text size="xs" className="mt-0.5 text-muted-foreground leading-snug">
                        {event.detail}
                      </Text>
                    ) : null}
                  </span>
                  <span className="shrink-0 pl-3 font-mono text-[11px] text-muted-foreground tabular-nums mt-px">
                    {formatTimelineTimestamp(event.timestampMs)}
                  </span>
                </>
              );
              return onSelectEvent ? (
                <button
                  key={i}
                  type="button"
                  onClick={() => onSelectEvent(event.timestampMs)}
                  className="flex w-full items-start gap-2.5 rounded-md px-2 py-1.5 -mx-1 text-left hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {row}
                </button>
              ) : (
                <div key={i} className="flex w-full items-start gap-2.5 px-2 py-1.5">
                  {row}
                </div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {onViewAnalysis && (
        <button
          type="button"
          onClick={onViewAnalysis}
          className="mt-3 -ml-1 inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          View full analysis
          <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
};
